import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { api, isCanceled } from "../api/client";

// Preserve the existing setPayload(...); reFetch() contract: requests start
// after the updated options have committed, rather than capturing old form data.
const useFetch = (options) => {
  const { trigger = false } = options;
  const autoRead = trigger && options.safeRead === true && (options.method || "GET").toUpperCase() === "GET";
  const readKey = autoRead ? JSON.stringify([options.url, options.params]) : null;
  const optionsRef = useRef(options);
  const controllerRef = useRef(null);
  const [run, setRun] = useState(0);
  const [state, setState] = useState({
    response: null, error: null, status: null, loading: Boolean(trigger),
  });

  useEffect(() => {
    optionsRef.current = options;
  });

  const reFetch = useCallback(() => setRun((value) => value + 1), []);
  useEffect(() => {
    const refresh = () => { if ((optionsRef.current.method || "GET").toUpperCase() === "GET" && optionsRef.current.trigger) reFetch(); };
    window.addEventListener("attar:records-changed", refresh);
    return () => window.removeEventListener("attar:records-changed", refresh);
  }, [reFetch]);
  const cancelRequest = useCallback(() => controllerRef.current?.abort(), []);

  useEffect(() => {
    if (!trigger && run === 0) return undefined;
    const current = optionsRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    let active = true;

    setState((previous) => ({
      ...previous, error: null, status: null, loading: true,
    }));

    api.request({
      method: current.method,
      url: current.url,
      data: current.data,
      params: current.params,
      headers: current.headers,
      auth: !current.noHeader,
      safeRead: current.safeRead === true,
      responseType: current.responseType,
      signal: controller.signal,
    }).then((result) => {
      if (!active || controller.signal.aborted) return;
      setState({
        response: result.data, status: result.status, error: null, loading: false,
      });
      current.setter?.(result.data);
      current.caller?.reFetch();
      current.argFunc?.(result.data);
    }).catch((error) => {
      if (!active) return;
      if (isCanceled(error) || controller.signal.aborted) {
        setState((previous) => ({ ...previous, loading: false }));
        return;
      }
      setState((previous) => ({
        ...previous, error, status: error.status ?? null, loading: false,
      }));
      if (error.status === 401 && !current.noHeader) return;
      if (current.errMessage && ![400, 403, 429].includes(error.status)) {
        current.errMessage(error);
      } else {
        toast.error(error.message, {
          position: "bottom-left", rtl: true, autoClose: 4000,
        });
      }
    });

    return () => {
      active = false;
      controller.abort();
      if (controllerRef.current === controller) controllerRef.current = null;
    };
  }, [run, trigger, readKey]);

  return { ...state, cancelRequest, reFetch, autoRead };
};

export default useFetch;
